import { AddressSuggestions } from "apps/visits/core/components/address-suggestion/address-suggestions";
import type { AddressSuggestion } from "apps/visits/core/models/address-suggestion";
import type { VoivodeshipWithoutPolishCharacters } from "apps/visits/core/types/voivodeship";
import { normalizeString } from "apps/visits/packages/shared/utils/string";
import { Card } from "apps/visits/packages/ui/src/components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useTrackPageViews } from "@/core/modules/tracking/gtm";
import { useLastPageView } from "@/core/modules/tracking/use-last-page-view";
import { Footer } from "@/core/shared/v2/footer";
import { Header } from "@/core/shared/v2/header";
import { Button } from "@/core/shared/v2/ui/button";
import { purchaseProductsAddressSelectionEvent, safeTrackEvent } from "../../tracking/events";
import { generateVoivodeshipLink } from "../utils/generate-voivodeship-link";

export const AddressSelection = () => {
  const { t } = useTranslation("translation", { keyPrefix: "VOUCHERS_DISTRIBUTION" });
  const [selectedAddress, setSelectedAddress] = useState<AddressSuggestion | null>(null);
  const history = useHistory();
  useTrackPageViews();
  const { getLastPageView, setLastPageView } = useLastPageView();

  const handleAddressSelect = (address: AddressSuggestion) => {
    setSelectedAddress(address);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleInputChange = (_value: string) => {
    if (selectedAddress) setSelectedAddress(null);
  };

  const handleContinue = () => {
    if (selectedAddress) {
      const voivodeship = selectedAddress.state;
      const normalizedVoivodeship = normalizeString(voivodeship) as VoivodeshipWithoutPolishCharacters;
      const link = generateVoivodeshipLink(normalizedVoivodeship);
      const fullAddress = `${selectedAddress.street} ${selectedAddress.houseNumber}, ${selectedAddress.zip} ${selectedAddress.city}, ${selectedAddress.state}`;

      const lastPageView = getLastPageView();
      if (lastPageView) {
        setLastPageView({
          ...lastPageView,
          selected_city: selectedAddress.city,
          selected_address: fullAddress,
          zone_id: null,
        });
      }

      safeTrackEvent(
        () =>
          purchaseProductsAddressSelectionEvent({
            selectedCity: selectedAddress.city,
            selectedAddress: fullAddress,
          }),
        "purchase_products_address_selection",
        "address-selection handleContinue",
      );

      // SPA navigation: the root `VouchersDistribution` component observes URL
      // changes, fetches the matching distribution's data (incl. CMS fragments)
      // via API, and swaps the Router with the new `/pp/<slug>` basename.
      // This keeps GTM's dataLayer session alive — a full page load would
      // reset it.
      setTimeout(() => history.push(link), 250);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-primary-foreground">
      <Header />
      <div className="flex flex-grow flex-col">
        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col items-center justify-center pb-48 max-lg:px-3">
          <div className="mb-8">
            <h1 className="mb-1 text-center font-serif text-2xl lg:text-4xl">{t("ADDRESS_SELECTION.TITLE")}</h1>
            <p className="mx-auto max-w-xl text-center">{t("ADDRESS_SELECTION.SUBTITLE")}</p>
          </div>
          <Card className="mx-auto w-full max-w-xl gap-5 rounded-2xl border bg-background p-5 shadow-sm">
            <AddressSuggestions
              label={t("ADDRESS_SELECTION.LABEL")}
              placeholder={t("ADDRESS_SELECTION.PLACEHOLDER")}
              required
              onSelect={handleAddressSelect}
              className="w-full"
              onInputChange={handleInputChange}
            />
            <div className="text-center">
              <Button className="w-full" size="lg" onClick={handleContinue}>
                {t("ADDRESS_SELECTION.CONTINUE_BUTTON")}
              </Button>
            </div>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};
